import type { EventCalendarProps as MuiEventCalendarProps } from '@mui/x-scheduler/event-calendar';
import { frFR } from '@mui/x-scheduler/locales';
import { type ReactNode, useMemo } from 'react';

export type EventCalendarView = 'month' | 'week' | 'day' | 'agenda';

export type EventCalendarColor =
  | 'red'
  | 'pink'
  | 'purple'
  | 'indigo'
  | 'blue'
  | 'teal'
  | 'green'
  | 'lime'
  | 'amber'
  | 'orange'
  | 'grey';

export type EventCalendarEventVariant = 'default' | 'reminder';

export type EventCalendarCategory = {
  id: string;
  label: string;
  color: EventCalendarColor;
};

export type EventCalendarEvent = {
  id: string;
  title: string;
  /** ISO date-time. */
  start: string;
  /** ISO date-time. For an all-day event, any time on the last day. */
  end: string;
  allDay?: boolean;
  /** Id of an entry of `categories`: gives the default color and the legend / filter entry. */
  categoryId: string;
  /** Overrides the category color. */
  color?: EventCalendarColor;
  /** `reminder` prefixes the event with the reminder bell. */
  variant?: EventCalendarEventVariant;
};

export type EventCalendarProps = {
  events: EventCalendarEvent[];
  categories: EventCalendarCategory[];
  view: EventCalendarView;
  onViewChange: (view: EventCalendarView) => void;
  visibleDate: Date;
  onVisibleDateChange: (date: Date) => void;
  /** Fired when an event is activated (click, Enter). `anchor` stays mounted to position a popover. */
  onEventClick?: (eventId: string, anchor: HTMLElement | null) => void;
  /** From `sm` up, takes the remaining height of a `fixedHeader` page instead of a viewport-based height. */
  fill?: boolean;
  /** Message shown above the calendar, e.g. an error `Alert` with its recovery action. */
  banner?: ReactNode;
  /** Shows a progress bar over the grid while events are fetched. */
  loading?: boolean;
  'aria-label'?: string;
  'data-testid'?: string;
};

type SchedulerEvent = {
  id: string;
  title: string;
  start: string;
  end: string;
  allDay?: boolean;
  resource: string;
  color?: EventCalendarColor;
  className?: string;
  readOnly: true;
};

type SchedulerResource = {
  id: string;
  title: string;
  eventColor: EventCalendarColor;
};

type MuiProps = MuiEventCalendarProps<SchedulerEvent, SchedulerResource>;

type UseEventCalendarResult = {
  events: SchedulerEvent[];
  resources: SchedulerResource[];
  localeText: MuiProps['localeText'];
  handleViewChange: NonNullable<MuiProps['onViewChange']>;
  handleVisibleDateChange: NonNullable<MuiProps['onVisibleDateChange']>;
  handleEventEditingStart: NonNullable<MuiProps['onEventEditingStart']>;
};

export const EVENT_CALENDAR_VIEWS: EventCalendarView[] = ['month', 'week', 'day', 'agenda'];

export const REMINDER_EVENT_CLASS = 'EventCalendar-reminder';

/** Class of an event whose color overrides its category: `${CATEGORY_ACCENT_CLASS}-${categoryColor}`. */
export const CATEGORY_ACCENT_CLASS = 'EventCalendar-categoryAccent';

const LOCALE_TEXT: MuiProps['localeText'] = {
  ...frFR.components.MuiEventCalendar.defaultProps.localeText,
  resourcesLabel: 'Catégories',
  hiddenEvents: (count: number) => `+ ${count} autre${count > 1 ? 's' : ''}`,
  agendaViewEmptyStateLabel: 'Aucun événement sur cette période',
  calendarContentAriaLabel: 'Contenu du calendrier',
  openMenu: 'Vues et filtres',
  nextTimeSpan: (timeSpan: string) => `${timeSpan} suivant`,
  previousTimeSpan: (timeSpan: string) => `${timeSpan} précédent`,
};

function isEventCalendarView(view: string): view is EventCalendarView {
  return (EVENT_CALENDAR_VIEWS as string[]).includes(view);
}

function getClassName(event: EventCalendarEvent, categoryColor: EventCalendarColor | undefined) {
  const classNames = [
    event.variant === 'reminder' ? REMINDER_EVENT_CLASS : null,
    // A custom color hides the kind of event: an accent in the category color recalls it.
    event.color && categoryColor && event.color !== categoryColor ? `${CATEGORY_ACCENT_CLASS}-${categoryColor}` : null,
  ].filter(Boolean);

  return classNames.length > 0 ? classNames.join(' ') : undefined;
}

export function toSchedulerEvent(event: EventCalendarEvent, categoryColor?: EventCalendarColor): SchedulerEvent {
  return {
    id: event.id,
    title: event.title,
    start: event.start,
    end: event.end,
    allDay: event.allDay,
    resource: event.categoryId,
    color: event.color,
    className: getClassName(event, categoryColor),
    readOnly: true,
  };
}

export function useEventCalendar(props: EventCalendarProps): UseEventCalendarResult {
  const events = useMemo(() => {
    const categoryColors = new Map(props.categories.map((category) => [category.id, category.color]));
    return props.events.map((event) => toSchedulerEvent(event, categoryColors.get(event.categoryId)));
  }, [props.events, props.categories]);

  const resources = useMemo<SchedulerResource[]>(
    () => props.categories.map((category) => ({ id: category.id, title: category.label, eventColor: category.color })),
    [props.categories],
  );

  const handleViewChange: UseEventCalendarResult['handleViewChange'] = (view) => {
    if (isEventCalendarView(view)) {
      props.onViewChange(view);
    }
  };

  const handleVisibleDateChange: UseEventCalendarResult['handleVisibleDateChange'] = (date) => {
    props.onVisibleDateChange(new Date(date as Date));
  };

  // The calendar is read-only: the built-in dialog is replaced by the consumer's own detail UI.
  const handleEventEditingStart: UseEventCalendarResult['handleEventEditingStart'] = (occurrence, eventDetails) => {
    eventDetails.cancel();

    if (eventDetails.reason === 'creation') {
      return;
    }

    props.onEventClick?.(String(occurrence.id), eventDetails.anchor ?? null);
  };

  return {
    events,
    resources,
    localeText: LOCALE_TEXT,
    handleViewChange,
    handleVisibleDateChange,
    handleEventEditingStart,
  };
}
